import axios from 'axios';
import { cccApiUrl } from '../config';

/** Do not throw on 4xx — login returns error JSON in body */
const cccHttp = axios.create({
  validateStatus: (status) => status < 500,
});

const apiUrl = (path: string) => cccApiUrl(path);

export type CccLoginResult = {
  success: boolean;
  challenge?: 'NEW_PASSWORD_REQUIRED' | string;
  session?: string;
  message?: string;
  jwt_token?: string;
  token?: string;
  user?: Record<string, unknown>;
  tenant?: string;
};

function persistSession(data: CccLoginResult) {
  if (typeof window === 'undefined') return;
  try {
    const token = data.jwt_token || data.token;
    if (token) {
      localStorage.setItem('jwt_token', token);
      localStorage.setItem('access_token', token);
    }
    if (data.user && typeof data.user === 'object') {
      const raw = JSON.stringify(data.user);
      localStorage.setItem('user', raw);
      localStorage.setItem('cognito_user', raw);
      if (data.user.tenant != null) {
        localStorage.setItem('user_tenant', String(data.user.tenant));
      }
    }
  } catch {
    /* storage blocked */
  }
}

function normalizeAxiosError(err: unknown): CccLoginResult {
  if (axios.isAxiosError(err)) {
    if (err.response?.data && typeof err.response.data === 'object') {
      return err.response.data as CccLoginResult;
    }
    if (err.code === 'ERR_NETWORK' || !err.response) {
      return {
        success: false,
        message:
          'Não foi possível contactar o servidor CCC. Confirme que o backend CCC está a correr (porta 5001).',
      };
    }
  }
  return { success: false, message: 'Erro no login. Tente novamente.' };
}

export const cccAuthApi = {
  async login(username: string, password: string): Promise<CccLoginResult> {
    try {
      const { data, status } = await cccHttp.post(
        `${apiUrl('/api/ccc/auth/login')}`,
        { username, password }
      );
      const body = (data || {}) as CccLoginResult;
      if (status >= 400 && !body.message) {
        body.success = false;
        body.message =
          status === 403
            ? 'Acesso reservado ao CallCenter Comercial e Comercial'
            : 'Credenciais inválidas ou utilizador não encontrado';
      }
      if (body?.success && (body.jwt_token || body.token)) {
        persistSession(body);
      }
      return body;
    } catch (err) {
      return normalizeAxiosError(err);
    }
  },

  async respondToAuthChallenge(
    username: string,
    session: string,
    newPassword: string,
    challengeName = 'NEW_PASSWORD_REQUIRED'
  ): Promise<CccLoginResult> {
    try {
      const { data, status } = await cccHttp.post(
        `${apiUrl('/api/ccc/auth/respond_to_auth_challenge')}`,
        {
          username,
          session,
          new_password: newPassword,
          challenge_name: challengeName,
        }
      );
      const body = (data || {}) as CccLoginResult;
      if (status >= 400 && !body.message) {
        body.success = false;
        body.message = 'Não foi possível definir a nova palavra-passe';
      }
      if (body?.success && (body.jwt_token || body.token)) {
        persistSession(body);
      }
      return body;
    } catch (err) {
      return normalizeAxiosError(err);
    }
  },

  async getMe(): Promise<Record<string, unknown> | null> {
    if (typeof window === 'undefined') return null;
    let token: string | null = null;
    try {
      token =
        localStorage.getItem('jwt_token') || localStorage.getItem('access_token');
    } catch {
      return null;
    }
    if (!token) return null;
    try {
      const { data, status } = await cccHttp.get(apiUrl('/api/ccc/auth/me'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (status === 200 && data?.success && data.user) {
        return data.user as Record<string, unknown>;
      }
    } catch {
      /* ignore */
    }
    return null;
  },

  logout() {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem('jwt_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      localStorage.removeItem('cognito_user');
      localStorage.removeItem('user_tenant');
      localStorage.removeItem('user_role');
      localStorage.removeItem('user_business_area');
    } catch {
      /* ignore */
    }
  },
};

export default cccAuthApi;
